/**
 * Shared hook for loading the Freshpaint SDK.
 * Used by both lyrahealthwebapp and Lyraweb-mobile (web routes only).
 */
import { useEffect, useRef } from 'react'

import { useScript } from '../../common/hooks/useScript'
import {
  enableFreshpaintRouteBlocking,
  FRESHPAINT_ENV_ID,
  FRESHPAINT_SDK_URL,
  initializeFreshpaintQueue,
  shouldInitializeFreshpaint,
  UseFreshpaint,
} from '../utils'

/**
 * Hook for loading the Freshpaint SDK.
 * Initializes the command queue proxy first, then loads the SDK script.
 * After SDK loads, enables route-based blocking for tracking.
 */
export const useFreshpaintSDK: UseFreshpaint = (enabled) => {
  const shouldLoad = shouldInitializeFreshpaint(enabled)
  const hasInitialized = useRef(false)
  const hasInstalledGuards = useRef(false)

  // Enable route blocking and initialize the command queue proxy before loading the SDK
  useEffect(() => {
    if (shouldLoad && !hasInitialized.current) {
      enableFreshpaintRouteBlocking()
      initializeFreshpaintQueue(FRESHPAINT_ENV_ID, { disable_target_text_capture: true })
      hasInitialized.current = true
    }
  }, [shouldLoad])

  // Load the actual SDK script after queue is initialized
  const scriptStatus = useScript(shouldLoad ? FRESHPAINT_SDK_URL : null, false, undefined)
  const isReady = scriptStatus === 'ready' && !!window.freshpaint

  // Log when SDK loads (only once)
  useEffect(() => {
    if (isReady && !hasInstalledGuards.current) {
      hasInstalledGuards.current = true
      console.debug(
        `[Freshpaint] ✅ SDK LOADED! Version: ${window.freshpaint?.__SV}, __loaded: ${window.freshpaint?.__loaded}`,
      )
    }
  }, [isReady])

  return isReady
}
